import type { Quest, QuestPerms } from 'types/quests'

type TeamMember = {
    id?: string | number
}

type UsersTeam = {
    user_id?: string | number
    status?: string
}

type UserWithTeams = {
    id?: string | number
    space_teams?: TeamLike[]
    space_team_ids?: Array<string | number>
}

type ReplyPolicyUser = UserWithTeams

type ReplyPolicyQuest = Partial<Quest>

type ReplyPolicyAnswer = {
    child_quests?: ReplyPolicyQuest[]
}

type ResolveReplyPolicyQuest = (quest: ReplyPolicyQuest) => ReplyPolicyQuest | null | undefined

export type TeamLike = {
    id?: string | number
    admin_id?: string | number
    public?: boolean
    private_stub?: boolean
    who_can_post_threads?: string | null
    who_can_reply_threads?: string | null
    users?: TeamMember[]
    users_teams?: UsersTeam | UsersTeam[]
}

type TeamPermsOptions = {
    team?: TeamLike | null
    publicTeams?: TeamLike[] | null
    userStreams?: TeamLike[] | null
}

export function isTeamAdmin(team?: TeamLike | null, user?: UserWithTeams | null) {
    if (!team || !user) return false
    return String(team.admin_id) === String(user.id)
}

export function isTeamMember(team?: TeamLike | null, user?: UserWithTeams | null) {
    if (!team || !user) return false
    if (isTeamAdmin(team, user)) return true
    if (team.private_stub) return false

    const userId = String(user.id)
    const teamId = String(team.id)

    const teamUsers = Array.isArray(team.users) ? team.users : []
    if (teamUsers.some(member => String(member.id) === userId)) return true

    const usersTeams = Array.isArray(team.users_teams)
        ? team.users_teams
        : team.users_teams
        ? [team.users_teams]
        : []
    if (
        usersTeams.some(usersTeam => {
            const status = usersTeam?.status
            return String(usersTeam?.user_id) === userId && (!status || status === 'accepted')
        })
    ) {
        return true
    }

    const userTeams = user.space_teams || []
    if (userTeams.some(userTeam => String(userTeam.id) === teamId)) return true

    const userTeamIds = user.space_team_ids || []
    return userTeamIds.some(id => String(id) === teamId)
}

export function teamPermissionRule(team?: TeamLike | null, rule?: string | null) {
    const normalizedRule = (rule || 'anyone').toString()
    return team?.public !== true && normalizedRule === 'anyone' ? 'members' : normalizedRule
}

// A signed-in non-member facing an open channel (public, open joining) where
// only members post: joining is what lets them post. Not for admin-only
// channels, where joining wouldn't help.
export function needsChannelJoinToPost(
    team?: (TeamLike & { standing_invitations_allowed?: boolean | null }) | null,
    user?: UserWithTeams | null,
) {
    if (!team || !user?.id) return false
    if (team.public !== true || team.standing_invitations_allowed !== true) return false
    if (teamPermissionRule(team, team.who_can_post_threads) !== 'members') return false
    return !isTeamMember(team, user)
}

export function canUserPostToTeam(team?: TeamLike | null, user?: UserWithTeams | null) {
    if (!team || !user) return false
    const rule = teamPermissionRule(team, team.who_can_post_threads)
    if (rule === 'admin') return isTeamAdmin(team, user)
    if (rule === 'members') return isTeamMember(team, user)
    return true
}

// Members-only main thread in a channel (members_only_replies): a signed-in
// non-member other than the thread author has to join the channel first.
// Signed-out visitors are left to the signup gate.
export function needsChannelJoinToReply(
    quest?: ReplyPolicyQuest | null,
    user?: ReplyPolicyUser | null,
) {
    if (!quest?.members_only_replies || !quest.team || !user?.id) return false
    if (quest.user_id && String(quest.user_id) === String(user.id)) return false
    // Only offer the join when joining is enough: an author-only thread, or a
    // channel where only admins reply, stays closed to new members too.
    if (quest.replies_enabled === false) return false
    const team = quest.team as TeamLike
    if (teamPermissionRule(team, team.who_can_reply_threads) === 'admin') return false
    return !isTeamMember(team, user)
}

export function canUserReplyInMainThread(
    quest?: ReplyPolicyQuest | null,
    user?: ReplyPolicyUser | null,
) {
    if (quest?.replies_enabled !== false) return !needsChannelJoinToReply(quest, user)
    if (!user?.id || !quest?.user_id) return false
    return String(quest.user_id) === String(user.id)
}

export function replyTargetQuestForAnswer(
    answer: ReplyPolicyAnswer | null | undefined,
    quest: ReplyPolicyQuest | null | undefined,
    isTitle: boolean,
    resolveQuest?: ResolveReplyPolicyQuest,
) {
    if (isTitle) return quest
    const childQuest = answer?.child_quests?.[0]
    if (!childQuest?.id || !resolveQuest) return childQuest
    return resolveQuest(childQuest) || childQuest
}

export function canUserReplyToAnswer(
    answer: ReplyPolicyAnswer | null | undefined,
    quest: ReplyPolicyQuest | null | undefined,
    isTitle: boolean,
    user?: ReplyPolicyUser | null,
    resolveQuest?: ResolveReplyPolicyQuest,
) {
    const targetQuest = replyTargetQuestForAnswer(answer, quest, isTitle, resolveQuest)
    return !targetQuest || canUserReplyInMainThread(targetQuest, user)
}

export function teamPermsFromFilterTeamId(
    teamId: string | number,
    user?: UserWithTeams | null,
    options: TeamPermsOptions = {},
): QuestPerms {
    const normalizedTeamId = String(teamId)
    const providedTeam =
        options.team && String(options.team.id) === normalizedTeamId ? options.team : null
    const memberTeam = user?.space_teams?.find(team => String(team.id) === normalizedTeamId)
    const publicTeam = options.publicTeams?.find(team => String(team.id) === normalizedTeamId)
    const storeTeam = options.userStreams?.find?.(team => String(team.id) === normalizedTeamId)
    const team = providedTeam || memberTeam || storeTeam || publicTeam || null

    const perms: QuestPerms = {
        team_id: null,
        team: null,
        private: null,
        public: null,
        join_gate_team_id: null,
    }

    if (team?.private_stub) {
        perms.private = true
    } else if (!team || canUserPostToTeam(team, user)) {
        perms.team_id = normalizedTeamId
        perms.team = team
    } else {
        perms.private = true
        perms.join_gate_team_id = normalizedTeamId
    }

    return perms
}
